import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { notify } from "../utils/ToastUtils";
import { Button, Checkbox, Select, MenuItem, TextField, InputAdornment } from "@mui/material";
import {
  FiUploadCloud,
  FiChevronDown,
} from "react-icons/fi";
import { 
  Settings, 
  Save, 
  Info, 
  Briefcase, 
  Link as LinkIcon, 
  User, 
  Mail, 
  CheckCircle2, 
  FileText, 
  ChevronRight, 
  Layout, 
  GripVertical, 
  Trash2, 
  Edit2,
  CircleAlert
} from 'lucide-react';
   import { Code2 } from "lucide-react";
import { ROLES } from '../utils/constants';

const variables = [
  "{{NAME}}",
  "{{ONBOARDING_LINK}}",
  "{{HR_NAME}}",
  "{{HR_MOBILE}}",
  "{{HR_EMAIL}}",
];



const ManageRoleConfigurations = ({ onSaveStateChange }) => {
  const [configRole, setConfigRole] = useState("Intern");
  const [configTemplate, setConfigTemplate] = useState("");
  const [configRequiredDocs, setConfigRequiredDocs] = useState([]);
  const [newDocState, setNewDocState] = useState({ name: "", file: null });
  const [configCustomFields, setConfigCustomFields] = useState([]);
  const [newFieldState, setNewFieldState] = useState({
    name: "",
    type: "Text",
    required: true,
  });
const [isFocused, setIsFocused] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const textFieldRef = useRef(null);
  const [pendingUploads, setPendingUploads] = useState({});
  const [pendingDeletes, setPendingDeletes] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  useEffect(() => {
    fetchRoleConfig("Intern");
  }, []);

  const fetchRoleConfig = async (role) => {
    setErrorMsg("");
    setPendingUploads({});
    setPendingDeletes([]);
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/admin/getRoleConfig`,
      );
      const config = data.find((c) => c.ROLE_NAME === role);
      if (config) {
        setConfigTemplate(config.EMAIL_TEMPLATE || "");
        let reqDocs = config.REQUIRED_DOCUMENTS_JSON || [];
        if (typeof reqDocs === "string") {
          try {
            reqDocs = JSON.parse(reqDocs);
          } catch (e) {
            reqDocs = [];
          }
        }
        const mappedReqDocs = reqDocs.map((d) =>
            typeof d === "string" ? { fieldName: d, linkedFile: null } : d,
        );
        setConfigRequiredDocs(mappedReqDocs);

        let custFields = config.CUSTOM_FIELDS_JSON || [];
        if (typeof custFields === "string") {
          try {
            custFields = JSON.parse(custFields);
          } catch (e) {
            custFields = [];
          }
        }
        
        const mappedCustFields = custFields.map((f) =>
            typeof f === "string"
              ? { fieldName: f, type: "Text", required: true }
              : f,
        );
        setConfigCustomFields(mappedCustFields);
      } else {
        setConfigTemplate("");
        setConfigRequiredDocs([]);
        setConfigCustomFields([]);
      }
    } catch (e) {
      console.error(e);
    }
  };
  const insertVariable = (variable) => {
    const textarea = textFieldRef.current;
    if (textarea) {
      const start = textarea.selectionStart || 0;
      const end = textarea.selectionEnd || 0;
      const currentValue = textarea.value;
      
      const newText = currentValue.substring(0, start) + variable + currentValue.substring(end);
      setConfigTemplate(newText);
      
      // Restore cursor position after state updates
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + variable.length, start + variable.length);
      }, 0);
    } else {
      setConfigTemplate((prev) => prev + variable);
    }
  };
  const handleRoleChange = (e) => {
    const newRole = e.target.value;
    setConfigRole(newRole);
    fetchRoleConfig(newRole);
  };

  const handleSaveConfig = async () => {
    setErrorMsg("");

    if (!configTemplate || !configTemplate.trim()) {
      setErrorMsg("Email HTML Template cannot be empty");
      return;
    }

    // Validate HTML tags
    const voidTags = [
      "area", "base", "br", "col", "embed", "hr", "img", "input",
      "link", "meta", "param", "source", "track", "wbr",
    ];
    const tagRegex = /<\/?([a-zA-Z0-9]+)[^>]*>/g;
    const stack = [];
    let match;
    let htmlValid = true;
    while ((match = tagRegex.exec(configTemplate)) !== null) {
      const fullTag = match[0];
      const tagName = match[1].toLowerCase();
      const isClosingTag = fullTag.startsWith("</");

      if (isClosingTag) {
        if (stack.length === 0 || stack[stack.length - 1] !== tagName) {
          htmlValid = false;
          break;
        }
        stack.pop();
      } else if (!voidTags.includes(tagName) && !fullTag.endsWith("/>")) {
        stack.push(tagName);
      }
    }
    if (!htmlValid || stack.length > 0) {
      setErrorMsg("Invalid HTML: Mismatched or unclosed tags detected.");
      return;
    }

    // Validate Placeholders
    const allowed = [
      "NAME", "HR_NAME", "HR_MOBILE", "HR_EMAIL", "ONBOARDING_LINK",
    ];

    // 1. Check for malformed formatting of allowed keywords
    for (const key of allowed) {
      const regex = new RegExp(`\\{*\\s*\\b${key}\\b\\s*\\}*`, "g");
      let m;
      while ((m = regex.exec(configTemplate)) !== null) {
        const matchStr = m[0];
        if (!matchStr.includes("{") && !matchStr.includes("}")) continue;
        const trimmed = matchStr.replace(/\s+/g, "");
        if (trimmed !== `{{${key}}}`) {
          setErrorMsg(`Malformed placeholder detected: "${matchStr.trim()}". It should be exactly {{${key}}}`);
          return;
        }
      }
    }

    // 2. Check for unknown placeholders inside {{ }}
    const placeholderRegex = /{{(.*?)}}/g;
    let pMatch;
    while ((pMatch = placeholderRegex.exec(configTemplate)) !== null) {
      const key = pMatch[1].trim();
      if (!allowed.includes(key)) {
        setErrorMsg(`Invalid placeholder detected: {{${key}}}. Allowed: {{NAME}}, {{ONBOARDING_LINK}}, {{HR_NAME}}, {{HR_MOBILE}}, {{HR_EMAIL}}`);
        return;
      }
    }

    // 3. Check for stray {{ or }}
    const withoutValidPlaceholders = configTemplate.replace(/{{.*?}}/g, "");
    if (withoutValidPlaceholders.includes("{{")) {
      setErrorMsg("Found an opening '{{' without a matching '}}'.");
      return;
    }
    if (withoutValidPlaceholders.includes("}}")) {
      setErrorMsg("Found a closing '}}' without a matching '{{'.");
      return;
    }

    try {
      setIsSaving(true);
      if (onSaveStateChange) onSaveStateChange(true);
      const formData = new FormData();
      formData.append("roleName", configRole);
      formData.append("emailTemplate", configTemplate);
      formData.append("requiredDocuments", JSON.stringify(configRequiredDocs));
      formData.append("customFields", JSON.stringify(configCustomFields));
      formData.append("pendingDeletes", JSON.stringify(pendingDeletes));

      for (const [docName, file] of Object.entries(pendingUploads)) {
        if (file) {
          formData.append("attachments", file, file.name);
        }
      }

      await axios.post(
        `${process.env.REACT_APP_BASE_URL}/admin/updateRoleConfig`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            authtoken: localStorage.getItem("authtoken"),
          },
        },
      );

      setPendingUploads({});
      setPendingDeletes([]);
      notify.success("Role configuration saved!");
    } catch (e) {
      setErrorMsg("Failed to save configuration.");
    } finally {
      setIsSaving(false);
      if (onSaveStateChange) onSaveStateChange(false);
    }
  };

  const handleUploadForDoc = async (e, docName) => {
    const file = e.target.files[0];
    if (!file) return;

    const existingDoc = configRequiredDocs.find((d) => d.fieldName === docName);
    if (existingDoc && existingDoc.linkedFile) {
      setPendingDeletes((prev) => [...prev, existingDoc.linkedFile]);
    }

    setPendingUploads((prev) => ({ ...prev, [docName]: file }));
    setConfigRequiredDocs(
      configRequiredDocs.map((d) =>
        d.fieldName === docName ? { ...d, linkedFile: file.name } : d,
      ),
    );
  };

  const addRequiredDoc = async () => {
    if (
      newDocState.name.trim() &&
      !configRequiredDocs.some((d) => d.fieldName === newDocState.name.trim())
    ) {
      let linkedFileName = null;
      if (newDocState.file) {
        const formData = new FormData();
        formData.append("attachment", newDocState.file);
        formData.append("roleName", configRole);
        formData.append("fieldName", newDocState.name.trim());
        linkedFileName = newDocState.file.name;
        setPendingUploads((prev) => ({
          ...prev,
          [newDocState.name.trim()]: newDocState.file,
        }));
      }
      setConfigRequiredDocs([
        ...configRequiredDocs,
        { fieldName: newDocState.name.trim(), linkedFile: linkedFileName },
      ]);
      setNewDocState({ name: "", file: null });
    }
  };

  const removeRequiredDoc = async (docName) => {
    const doc = configRequiredDocs.find((d) => d.fieldName === docName);
    if (doc && doc.linkedFile) {
      setPendingDeletes((prev) => [...prev, doc.linkedFile]);
    }
    setPendingUploads((prev) => {
      const newUploads = { ...prev };
      delete newUploads[docName];
      return newUploads;
    });
    setConfigRequiredDocs(
      configRequiredDocs.filter((d) => d.fieldName !== docName),
    );
  };

  const addCustomField = () => {
    if (
      newFieldState.name.trim() &&
      !configCustomFields.some((f) => f.fieldName === newFieldState.name.trim())
    ) {
      setConfigCustomFields([
        ...configCustomFields,
        {
          fieldName: newFieldState.name.trim(),
          type: newFieldState.type,
          required: newFieldState.required,
        },
      ]);
      setNewFieldState((prev) => ({ ...prev, name: "" }));
    }
  };

  const removeCustomField = (fieldName) => {
    setConfigCustomFields(
      configCustomFields.filter((f) => f.fieldName !== fieldName),
    );
  };

  return (
    <div className="flex flex-col bg-white rounded-b-2xl relative">
      <button
        id="hidden-save-role-config-btn"
        className="hidden"
        onClick={handleSaveConfig}
      />
      {/* Top Action Bar */}
      <div className="flex flex-col gap-3 px-6 py-4 border-b border-slate-200 bg-white shrink-0">
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-4">
            <label className="text-sm font-semibold text-slate-700 whitespace-nowrap">
              Select Role
            </label>
            <Select
              value={configRole}
              onChange={handleRoleChange}
              size="small"
              startAdornment={
                <InputAdornment position="start">
                  <User size={18} className="text-blue-500" />
                </InputAdornment>
              }
              className="w-56 h-38 bg-white text-sm font-medium text-slate-700"
              sx={{
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#cbd5e1',
                  borderRadius: '0.5rem',
                },
                   '& .MuiSelect-select': {
                      height: '38px',
                      boxSizing: 'border-box',
                      display: 'flex',
                      alignItems: 'center',
                  },
              }}
            >
              {ROLES.map((role, i) => (
                <MenuItem key={i} value={role.value} sx={{ fontSize: '14px' }}>{role.label}</MenuItem>
              ))}
            </Select>
          </div>
          <div className="flex items-center gap-3">
              <button 
                className="h-9 px-4 text-[13px] font-semibold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
                onClick={() => fetchRoleConfig(configRole)}
                disabled={isSaving}
              >
                Reset
              </button>
              <button
                className="h-9 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-bold shadow-sm text-[13px] flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                onClick={handleSaveConfig}
                disabled={isSaving}
              >
                {isSaving ? (
                    <>
                        <div className="spinner-border spinner-border-sm text-white" role="status" style={{ width: '14px', height: '14px' }}></div>
                        Saving...
                    </>
                ) : (
                    <>
                        <Save size={14} />
                        Save Configuration
                    </>
                )}
              </button>
          </div>
        </div>
        {errorMsg && (
          <div className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-medium text-red-600 w-full">
            <CircleAlert size={16} className="shrink-0" />
            <span>{errorMsg}</span>
          </div>
        )}
      </div>
      <div className="flex flex-col lg:flex-row flex-1 min-h-0 overflow-y-auto lg:overflow-hidden p-4 gap-4 custom-scrollbar">


        {/* Right Main Content */}
        <div className="flex-1 overflow-y-auto flex flex-col gap-4 lg:gap-6 lg:pr-4 pb-10 custom-scrollbar">
          {/* Section 1: Email Template */}
 <div className="px-3 py-1">
    {/* Header */}
    <div className="flex items-start gap-4 mb-3">
      <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-100 font-bold text-blue-600">
        1
      </div>

      <div>
        <h3 className="text-[18px] font-bold text-slate-800">
          Email Template
        </h3>

        <p className="mt-1 text-[13px] text-slate-500">
          Customize the onboarding email sent to employees.
        </p>
      </div>
    </div>

    {/* Editor */}
 <div
  className={`rounded-xl bg-white ${
     isFocused
      ? "border-[1.5px] border-[#3b82f6] shadow-[0_0_0_1px_#1976d2]"
      : "border border-slate-200"
  }`}
>

      {/* Variables */}
      <div className="border-b border-slate-200 px-5 py-4">

        <div className="flex items-center gap-2 mb-3">
          <Code2 size={16} className="text-blue-600" />

          <span className="text-sm font-semibold text-slate-700">
            Available Variables
          </span>

          <span className="ml-auto text-xs text-slate-400">
            Click to insert
          </span>
        </div>

        <div className="flex flex-wrap gap-2">
          {variables.map((item) => (
            <button
              key={item}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => insertVariable(item)}
              className="rounded-md border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-700 transition-all hover:border-blue-400 hover:bg-blue-50 hover:text-blue-600"
            >
              {item}
            </button>
          ))}
        </div>
      </div>

      {/* Text Editor */}
      <TextField
        inputRef={textFieldRef}
        multiline
  rows={12}
  fullWidth
  value={configTemplate}
  onFocus={() => setIsFocused(true)}
  onBlur={() => setIsFocused(false)}
  onChange={(e) => {
    setConfigTemplate(e.target.value);
    setErrorMsg("");
  }}
        placeholder={`Dear {{NAME}},

Welcome to our organization.

Please complete your onboarding using the link below.

{{ONBOARDING_LINK}}

For any questions, please contact:

{{HR_NAME}}
{{HR_EMAIL}}
{{HR_MOBILE}}

Thank you.`}
        sx={{
          "& .MuiOutlinedInput-root": {
            borderRadius: 0,
            backgroundColor: "#fff",
            padding: 0,
            alignItems: "flex-start",
            fontFamily: "monospace",
          },

          /* Remove TextField border */
          "& .MuiOutlinedInput-notchedOutline": {
            border: "none",
          },

          "& textarea": {
            padding: "22px",
            fontSize: "14px",
            lineHeight: 1.8,
            color: "#334155",
          },
        }}
      />

      {/* Footer */}
      <div className="flex items-center justify-between border-t border-slate-200 px-5 py-3">

        <span className="text-xs text-slate-500">
          Variables are automatically replaced before sending the email.
        </span>

        <span className="rounded-md bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
          {configTemplate.length} Characters
        </span>

      </div>
    </div>

    {/* {errorMsg && (
      <p className="mt-3 text-sm text-red-500">
        {errorMsg}
      </p>
    )} */}
  </div>

          {/* Section 2: Required Documents */}
          <div className="px-3">
            <div className="flex items-start gap-4 mb-3">
               <div className="w-8 h-8 rounded-lg bg-blue-100 text-blue-600 font-bold flex items-center justify-center text-[15px]">2</div>
               <div>
                 <h3 className="text-[17px] font-bold text-slate-800 m-0 leading-tight">Required Documents</h3>
                 <p className="text-[13px] text-slate-500 mt-1 mb-0 font-medium">Specify the mandatory documents candidates must upload during onboarding.</p>
               </div>
            </div>
            
     {/* Input Row */}
<div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:items-end mb-4">

  {/* Document Name */}
  <div className="md:col-span-5">
    <label className="block mb-1.5 text-sm font-medium text-slate-700">
      Document Name <span className="text-red-500">*</span>
    </label>
    <TextField
      fullWidth
      size="small"
      value={newDocState.name}
      onChange={(e) =>
        setNewDocState((prev) => ({ ...prev, name: e.target.value }))
      }
      placeholder="Enter document name"
      sx={{
        '& .MuiOutlinedInput-root': {
          height: '38px',
          borderRadius: '8px',
          backgroundColor: '#fff',
          fontSize: '14px',
        },
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: '#E2E8F0',
        },
      }}
    />
  </div>

  {/* Upload File */}
  <div className="md:col-span-5">
    <label className="block mb-1.5 text-sm font-medium text-slate-700">
      Upload Document <span className="text-red-500">*</span>
    </label>

    <Button
      fullWidth
      component="label"
      variant="outlined"
      startIcon={!newDocState.file ? <FiUploadCloud size={16} /> : null}
      sx={{
        height: "38px",
        justifyContent: "flex-start",
        borderColor: newDocState.file ? "#10B981" : "#E2E8F0",
        color: newDocState.file ? "#059669" : "#475569",
        backgroundColor: newDocState.file ? "#ECFDF5" : "#FFFFFF",
        textTransform: "none",
        fontWeight: 500,
        borderRadius: "8px",
        fontSize: "13px",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      {newDocState.file
        ? newDocState.file.name.length > 28
          ? `${newDocState.file.name.substring(0, 28)}...`
          : newDocState.file.name
        : "Choose a file"}

      <input
        hidden
        type="file"
        onChange={(e) => {
          if (e.target.files?.[0]) {
            setNewDocState((prev) => ({
              ...prev,
              file: e.target.files[0],
            }));
          }
          e.target.value = null;
        }}
      />
    </Button>
  </div>

  {/* Add Button */}
  <div className="md:col-span-2">
    <Button
      fullWidth
      variant="contained"
      onClick={addRequiredDoc}
      disableElevation
      sx={{
        height: "38px",
        backgroundColor: "#2563eb",
        textTransform: "none",
        fontWeight: 600,
        borderRadius: "8px",
        fontSize: "14px",
        boxShadow: "none",
      }}
    >
      Add
    </Button>
  </div>

</div>
            {/* Table */}
            <div className="border border-slate-200 rounded-xl overflow-hidden">
               {/* Table header */}
               <div className="flex items-center px-4 py-3 border-b border-slate-200 text-[11px] font-bold text-slate-500 uppercase tracking-wider bg-white">
                  <div className="w-10"></div>
                  <div className="flex-1">DOCUMENT NAME</div>
                  <div className="flex-[2]">DESCRIPTION (OPTIONAL)</div>
                  <div className="w-24 text-right">ACTIONS</div>
               </div>
               {/* Table body */}
               {configRequiredDocs.length === 0 ? (
                  <div className="py-8 text-center text-slate-500 text-[13px] font-medium bg-slate-50/30">
                    No required documents added yet.
                  </div>
               ) : configRequiredDocs.map((doc, index) => (
                  <div key={index} className="flex items-center px-4 py-3.5 border-b border-slate-100 last:border-b-0 bg-white hover:bg-slate-50/50 transition-colors group">
                     <div className="w-10 text-[13px] text-slate-600 font-medium">
                        {index + 1}
                     </div>
                     <div className="flex-1 font-medium text-slate-800 text-[14px]">
                        {doc.fieldName}
                     </div>
                     <div className="flex-[2] text-slate-500 text-[13px] flex items-center gap-3">
                        {doc.linkedFile ? (
                           <>
                             <span className="truncate max-w-[150px]" title={doc.linkedFile}>{doc.linkedFile}</span>
                             <div className="relative inline-flex items-center">
                               <input
                                 type="file"
                                 className="opacity-0 absolute inset-0 w-full h-full cursor-pointer z-10"
                                 onChange={(e) => handleUploadForDoc(e, doc.fieldName)}
                               />
                               <span className="text-[12px] text-blue-500 hover:text-blue-700 font-semibold cursor-pointer underline underline-offset-2">Change</span>
                             </div>
                           </>
                        ) : (
                           <div className="relative inline-flex items-center">
                             <input
                               type="file"
                               className="opacity-0 absolute inset-0 w-full h-full cursor-pointer z-10"
                               onChange={(e) => handleUploadForDoc(e, doc.fieldName)}
                             />
                             <Button
                               variant="outlined"
                               size="small"
                               startIcon={!pendingUploads[doc.fieldName] ? <FiUploadCloud size={14} /> : null}
                               sx={{
                                 height: "28px",
                                 borderColor: pendingUploads[doc.fieldName] ? "#10B981" : "#CBD5E1",
                                 color: pendingUploads[doc.fieldName] ? "#059669" : "#3B82F6",
                                 textTransform: "none",
                                 fontWeight: 600,
                                 borderRadius: "6px",
                                 fontSize: "12px",
                                 padding: "0 10px"
                               }}
                             >
                               {pendingUploads[doc.fieldName] ? pendingUploads[doc.fieldName].name.length > 12 ? pendingUploads[doc.fieldName].name.substring(0, 12) + "..." : pendingUploads[doc.fieldName].name : "Upload File"}
                             </Button>
                           </div>
                        )}
                     </div>
                     <div className="w-24 flex justify-end">
                        <button onClick={() => removeRequiredDoc(doc.fieldName)} className="text-red-400 hover:text-red-600 p-1.5 rounded-md hover:bg-red-50 transition-colors">
                           <Trash2 size={16} />
                        </button>
                     </div>
                  </div>
               ))}

            </div>
          </div>
          
          {/* Section 3: Additional Information (Custom Fields) */}
          <div className="mb-2 px-3">
            <div className="flex items-start gap-4 mb-3">
               <div className="w-8 h-8 rounded-lg bg-blue-100 text-blue-600 font-bold flex items-center justify-center text-[15px]">3</div>
               <div>
                 <h3 className="text-[17px] font-bold text-slate-800 m-0 leading-tight">Additional Information (Custom Fields)</h3>
                 <p className="text-[13px] text-slate-500 mt-1 mb-0 font-medium">Add extra fields to collect role-specific information.</p>
               </div>
            </div>
            
            {/* Input row */}
       <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:items-end mb-4">

  {/* Field Name */}
  <div className="md:col-span-5">
    <label className="block mb-1.5 text-sm font-medium text-slate-700">
      Field Name <span className="text-red-500">*</span>
    </label>

    <TextField
      fullWidth
      size="small"
      value={newFieldState.name}
      onChange={(e) =>
        setNewFieldState((prev) => ({
          ...prev,
          name: e.target.value,
        }))
      }
      placeholder="Enter field name"
      sx={{
        '& .MuiOutlinedInput-root': {
          height: "38px",
          borderRadius: "8px",
          backgroundColor: "#fff",
          fontSize: "14px",
        },
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: "#E2E8F0",
        },
      }}
    />
  </div>

  {/* Field Type */}
  <div className="md:col-span-3">
    <label className="block mb-1.5 text-sm font-medium text-slate-700">
      Field Type <span className="text-red-500">*</span>
    </label>

    <Select
      fullWidth
      value={newFieldState.type}
      onChange={(e) =>
        setNewFieldState((prev) => ({
          ...prev,
          type: e.target.value,
        }))
      }
      size="small"
      sx={{
        height: "38px",
        borderRadius: "8px",
        backgroundColor: "#fff",
        fontSize: "14px",
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: "#E2E8F0",
        },
      }}
    >
      <MenuItem value="Text">Text Input</MenuItem>
      <MenuItem value="Number">Number</MenuItem>
      <MenuItem value="Date">Date Picker</MenuItem>
      <MenuItem value="File">File Upload</MenuItem>
    </Select>
  </div>

  {/* Required */}
  <div className="md:col-span-2">
    <label className="block mb-1.5 text-sm font-medium text-slate-700">
      Validation
    </label>

    <div className="h-[38px] flex items-center border border-slate-200 rounded-lg px-3 bg-white">
      <Checkbox
        checked={newFieldState.required}
        onChange={(e) =>
          setNewFieldState((prev) => ({
            ...prev,
            required: e.target.checked,
          }))
        }
        size="small"
        sx={{
          p: 0,
          mr: 1,
          color: "#3B82F6",
          "&.Mui-checked": {
            color: "#2563EB",
          },
        }}
      />
      <span className="text-sm text-slate-700 font-medium">
        Required
      </span>
    </div>
  </div>

  {/* Add Button */}
  <div className="md:col-span-2">
    <Button
      fullWidth
      variant="contained"
      onClick={addCustomField}
      disableElevation
      sx={{
        height: "38px",
        textTransform: "none",
        fontWeight: 600,
        backgroundColor: "#2563eb",
        borderRadius: "8px",
        boxShadow: "none",
      }}
    >
      Add Field
    </Button>
  </div>

</div>

            {/* Table */}
            <div className="border border-slate-200 rounded-xl overflow-hidden">
               {/* Table header */}
               <div className="flex items-center px-4 py-3 border-b border-slate-200 text-[11px] font-bold text-slate-500 uppercase tracking-wider bg-white">
                  <div className="flex-[2] pl-2">FIELD NAME</div>
                  <div className="flex-[1.5]">TYPE</div>
                  <div className="w-24 text-center">REQUIRED</div>
                  <div className="w-24 text-right">ACTIONS</div>
               </div>
               {/* Table body */}
               {configCustomFields.length === 0 ? (
                  <div className="py-8 text-center text-slate-500 text-[13px] font-medium bg-slate-50/30">
                    No custom fields added yet.
                  </div>
               ) : configCustomFields.map((field, index) => (
                  <div key={index} className="flex items-center px-4 py-3.5 border-b border-slate-100 last:border-b-0 bg-white hover:bg-slate-50/50 transition-colors">
                     <div className="flex-[2] font-medium text-slate-800 text-[14px] flex items-center gap-4 pl-2">
                        <div className="w-4 text-[13px] text-slate-500 font-medium">{index + 1}</div>
                        {field.fieldName}
                     </div>
                     <div className="flex-[1.5] text-slate-600 text-[13px] font-medium">
                        {field.type === 'Text' ? 'Text Input' : field.type}
                     </div>
                     <div className="w-24 flex justify-center">
                        {field.required ? (
                           <span className="bg-emerald-50 text-emerald-600 px-3 py-1 rounded-full text-[12px] font-bold tracking-wide">Yes</span>
                        ) : (
                           <span className="bg-slate-100 text-slate-600 px-3 py-1 rounded-full text-[12px] font-bold tracking-wide">No</span>
                        )}
                     </div>
                     <div className="w-24 flex justify-end gap-1">
                        <button className="text-blue-500 hover:text-blue-700 p-1.5 rounded-md hover:bg-blue-50 transition-colors">
                           <Edit2 size={16} />
                        </button>
                        <button onClick={() => removeCustomField(field.fieldName)} className="text-red-400 hover:text-red-600 p-1.5 rounded-md hover:bg-red-50 transition-colors">
                           <Trash2 size={16} />
                        </button>
                     </div>
                  </div>
               ))}
            </div>
          </div>
          
        </div>
      </div>

    </div>
  );
};

export default ManageRoleConfigurations;
